import cn from "classnames";
import React, { forwardRef, useContext, useMemo } from "react";
import { useSelector } from "react-redux";

import {
  chartActionsSelector,
  chartLoadersSelector,
  chartTabSelector,
  chartTreeCachedOpenSelector,
  chartTreeExpanderSelector,
  chartTreeFiltersSelector,
} from "redux/modules/common/chart/selectors";
import { IChartFilters, IChartTree } from "redux/modules/common/chart/types";

import { useRem } from "components/pages/Manufacturing/hooks/useRem";

import { ChartContext } from "../../Chart";
import ChartDateLine from "../ChartDateLine/ChartDateLine";
import { ChartLockedSpacer } from "../ChartLockedSpacer/ChartLockedSpacer";
import ChartScrollFAB from "../ChartScrollFAB/ChartScrollFAB";
import ChartTreeExpander from "../ChartTreeExpander/ChartTreeExpander";
import ChartTreeHeader from "../ChartTreeHeader/ChartTreeHeader";
import ChartTreeItem from "./ChartTreeItem";
import { FixedSizeTree as Tree } from "lib/vtree";
import AutoSizer from "react-virtualized-auto-sizer";
import EmptyPlaceholder from "shared/ui/layout/EmptyPlaceholder/EmptyPlaceholder";

import { CHART_TREE_LVL } from "../../constants";

import { useChartRunningLine } from "../../hooks/useChartRunningLine";

import { checkHasFilteringActions, checkHasFilters } from "./ChartTree.utils/checkHasFilters";
import { checkSatisfyFilters } from "./ChartTree.utils/checkSatisfyFilters";

import scheduleIcon from "images/icons/navigation/scheduleIcon.svg";

import styles from "./ChartTree.module.scss";

export interface IChartTreeProps {
  tree: IChartTree;
}

// @ts-ignore
const innerElementType = forwardRef(({ children, style, ...rest }, ref) => {
  const expander = useSelector(chartTreeExpanderSelector);
  const loaders = useSelector(chartLoadersSelector);
  const runningLineRef = useChartRunningLine();
  const filters = useSelector(chartTreeFiltersSelector);
  const hasProjectLvlSkipFilter = filters?.skipBranchLvl?.[CHART_TREE_LVL.PROJECT];
  // Если в списке только rootNode или rootNode и projectNode и строка проекта скрыта, список считается пустым
  const emptyListLength = hasProjectLvlSkipFilter ? 2 : 1;

  const treeCn = cn(styles.tree, {
    [styles.withPeriod]: !!expander,
    [styles.w7]: expander?.days,
    [styles.w22]: expander?.period,
    [styles.bgWhiteLight]: hasProjectLvlSkipFilter,
    [styles.bgLightWhite]: !hasProjectLvlSkipFilter,
  });
  return (
    // @ts-ignore
    <div ref={ref} {...rest} style={style} className={styles.wrapper}>
      <div ref={runningLineRef} className={styles.runningLine} id="chartRunningLine" />
      {/* @ts-ignore */}
      <ChartDateLine containerRef={ref} />
      <ChartTreeHeader />
      <div className={treeCn} style={{ height: `${style?.height}px` }}>
        {children?.length === emptyListLength ? (
          <EmptyPlaceholder text="Нет расценок удовлетворяющих фильтрам" textCenter img={scheduleIcon} />
        ) : (
          children
        )}
        <ChartTreeExpander />
        <ChartScrollFAB variant="left" />
      </div>
      {loaders?.blurred ? <ChartLockedSpacer /> : null}
    </div>
  );
});

export interface IChartTreeItemProps {
  tree: IChartTree;
}

const getNodeDefaultOpenState = ({
  node,
  filters,
  forceOpenState,
  cachedOpen,
}: {
  node: IChartTree;
  filters: IChartFilters;
  cachedOpen: Record<string, boolean>;
  forceOpenState: boolean | undefined;
}): boolean => {
  if (node.lvl === CHART_TREE_LVL.ROOT) return true;
  if (forceOpenState === false && !filters?.skipBranchLvl?.[CHART_TREE_LVL.PROJECT]) return false;
  if (node.lvl === CHART_TREE_LVL.PROJECT) return true;
  if (filters?.skipBranchLvl?.[CHART_TREE_LVL.PROJECT] && node.lvl === CHART_TREE_LVL.LSR) return true;
  if (forceOpenState === true) return true;
  return Boolean(cachedOpen?.[node._id]);
};

const ChartTree: React.FC<IChartTreeProps> = ({ tree }) => {
  const { containerRef, treeRef } = useContext(ChartContext);
  const filters = useSelector(chartTreeFiltersSelector);
  const actions = useSelector(chartActionsSelector);
  const cachedOpen = useSelector(chartTreeCachedOpenSelector);
  const diagramActions = useSelector(chartActionsSelector);
  const tab = useSelector(chartTabSelector);

  const getNodeData = (node: any, nestingLevel: number): any => ({
    data: {
      // Матчинг и key – "node._id"
      ...node,
      isLeaf: node.children.length === 0,
      isOpenByDefault: getNodeDefaultOpenState({
        node,
        filters: filters,
        cachedOpen,
        forceOpenState: diagramActions.default_tree_open_state!,
      }),
      nestingLevel,
    },
    nestingLevel,
    node,
  });

  const { REM } = useRem();

  const treeWalker = useMemo(() => {
    const hasFilters = checkHasFilters(filters);
    const hasFilteringActions = checkHasFilteringActions(actions);
    if (hasFilters) {
      return function* treeWalkerWithFilters(): any {
        const root = getNodeData(tree, 0);
        yield root;
        //@ts-ignore
        function* exploreNode(parentMeta, nestingLevel) {
          for (let i = 0; i < parentMeta.node.children.length; i++) {
            const child = getNodeData(parentMeta.node.children[i], nestingLevel + 1);
            if (checkSatisfyFilters(child.node, { filters, actions, tab })) {
              yield child;
            }
            yield* exploreNode(child, nestingLevel + 1);
          }
        }
        //@ts-ignore
        yield* exploreNode(root, 0);
      };
    } else {
      return function* treeWalker(): any {
        const skipBranchLvl = filters.skipBranchLvl || {};
        const root = getNodeData(tree, 0);
        yield root;

        while (true) {
          const parentMeta = yield;

          const skipChildren = parentMeta.nestingLevel !== 0 && skipBranchLvl[parentMeta.nestingLevel + 1] === true;
          if (!skipChildren) {
            for (let i = 0; i < parentMeta.node.children.length; i++) {
              const child = getNodeData(parentMeta.node.children[i], parentMeta.nestingLevel + 1);
              if (hasFilteringActions) {
                if (checkSatisfyFilters(child.node, { filters, actions, tab })) {
                  yield child;
                }
              } else {
                yield child;
              }
            }
          }
        }
      };
    }
  }, [tree, filters, actions, diagramActions.default_tree_open_state, tab]);

  if (!tree?._id) return null;

  return (
    <AutoSizer>
      {/* @ts-ignore */}
      {({ height, width }) => (
        <Tree
          ref={treeRef} // ref виртуального дерева
          treeWalker={treeWalker}
          itemSize={3 * REM}
          overscanCount={20}
          innerRef={containerRef} // ref htm-элемента дерева
          innerElementType={innerElementType}
          height={height}
          width={width}
        >
          {/* @ts-ignore */}
          {ChartTreeItem}
        </Tree>
      )}
    </AutoSizer>
  );
};

export default React.memo(ChartTree);
